import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import whatsappLogo from './whatsapp-logo.png';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const Contact: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [errors, setErrors] = useState({
    name: '',
    email: '',
    message: '',
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(false);
    setSubmitError(null);

    const newErrors = {
      name: '',
      email: '',
      message: '',
    };

    if (!name.trim()) {
      newErrors.name = 'Veuillez saisir votre nom.';
    }
    if (!email.trim()) {
      newErrors.email = 'Veuillez saisir votre adresse e-mail.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Adresse e-mail invalide.";
    }
    if (!message.trim()) {
      newErrors.message = 'Veuillez saisir votre message.';
    }

    setErrors(newErrors);

    const hasError = Object.values(newErrors).some((v) => v !== '');
    if (hasError) {
      return;
    }

    try {
      setSubmitting(true);

      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, message, orderNumber: orderNumber.trim() || undefined }),
      });

      if (!response.ok) {
        let errorMessage = "Erreur lors de l'envoi du message.";
        try {
          const data = await response.json();
          if (data && data.error) {
            errorMessage = data.error;
          }
        } catch {
          // ignore parse error
        }
        throw new Error(errorMessage);
      }

      setSubmitted(true);
      setName('');
      setEmail('');
      setOrderNumber('');
      setMessage('');
    } catch (error: any) {
      setSubmitError(error?.message || "Erreur lors de l'envoi du message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Contact
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Si vous avez une question sur un produit, une commande, la boutique, le S.A.V ou autre ? Remplissez le
        formulaire ci-dessous et nous reviendrons vers vous au plus vite.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          gap: 4,
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <TextField
            label="Nom"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={Boolean(errors.name)}
            helperText={errors.name}
          />
          <TextField
            label="E-mail"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={Boolean(errors.email)}
            helperText={errors.email}
          />
          <TextField
            label="Numéro de commande (optionnel)"
            fullWidth
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
          />
          <TextField
            label="Message"
            fullWidth
            multiline
            minRows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            error={Boolean(errors.message)}
            helperText={errors.message}
          />

          <Box sx={{ mt: 1 }}>
            <Button type="submit" variant="contained" color="primary" disabled={submitting}>
              {submitting ? 'Envoi en cours...' : 'Envoyer le message'}
            </Button>
          </Box>

          {submitError && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error">
                {submitError}
              </Alert>
            </Box>
          )}

          {submitted && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="success">
                Merci pour votre message ! Nous vous répondrons dans les plus brefs
                délais.
              </Alert>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="h6" gutterBottom>
            Coordonnées de la boutique
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Bratan Family - Boutique en ligne (sans magasin physique)
          </Typography>

          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="img"
              src={whatsappLogo}
              alt="WhatsApp"
              sx={{ width: 24, height: 24, borderRadius: '50%' }}
            />
            <Box>
              <Typography variant="subtitle2">Téléphone via WhatsApp</Typography>
              <Typography variant="body2" color="text.secondary">
                +32 456 96 20 71
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">E-mail</Typography>
            <Typography variant="body2" color="text.secondary">
              bratanfamily59@gmail.com
            </Typography>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Horaires</Typography>
            <Typography variant="body2" color="text.secondary">
              Réponse à vos messages : 8h00 à 20h00
            </Typography>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default Contact;
